import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { TableColumnWidthInfo } from '@devexpress/dx-react-grid';
import { TablePagination } from '@mui/material';
import type { IRowNode } from 'ag-grid-community';
import debounce from 'lodash.debounce';
import {
  buttonTriggerLateralFilter,
  DATE_FILTER_PARAMS,
  defaultFilter,
  defaultFilterValues,
  defaultTableInfo,
  findFilterKey,
  getTimeToDebounce,
  mapCampaingsRows,
} from '@/components/SearchTable/search-table-utils';
import { SearchTableRemoteProps } from '@/components/SearchTable/SearchTableRemote';
import HeaderWithTooltip from '@/components/UI/TableListEditable/HeaderWithTooltip/HeaderWithTooltip';
import { FILTER_TYPES, PAGE_SIZES, TABLE_ROW_SELECTION, type AlignValue } from '@/components/UI/TableListEditable/TableListEditableConstants';
import TableListEditableNew from '@/components/UI/TableListEditable/TableListEditableNew';
import {
  CAPITAL_ASC, CAPITAL_DESC, CREATED, CREATED_RAW, DESC_ORDER, filterField, HEADER_TO_FILTER, OVERVIEW_COLUMNS, STATUS, statusCampaign, statusSubCampaign, VANS_LABEL,
  UTC, VERSION_DATE, VERSION_DATE_RAW,
} from '@/helpers/constants';
import { buildOpensearchQueryData, opensearchFieldMapping, otherField } from '@/helpers/opensearch-utils';
import { columnsOrderMerge, mergeColumnExtensions, processSorting } from '@/helpers/tables-utils';
import { createRowLink } from '@/helpers/utils';
import useApiRequest from '@/hooks/useApiRequest';
import useAppLanguage from '@/hooks/useAppLanguage';
import useGoTo from '@/hooks/useGoTo';
import useToastContext from '@/hooks/useToastContext';
import campaignServices from '@/services/campaignServices';
import { CampaignInfoOverview, SubCampaignInfoOverview, GlobalFilters, VisibilityColumnsOverview } from '@/types';
import { ColumnConfig, ColumnSorting, TreeDataConfig } from '@/types/TableListEditable';
import { StatusFilterAgGrid, StatusFloatingFilter } from './CampaignRequestsStatusFilter';
import { buildColumnSorting, buildFilterModel, buildFilterOverview, buildSortOverview, mapColumn } from './CampaignRequestsTable.utils';
import { buildCampaignTitleCellRenderer } from './CampaignTitleCell/CampaignTitleCell';
import LateralFilterButton from './LateralFilterButton/LateralFilterButton';

const COLUMN_ID = 'id';
const DEFAULT_COLUMN_WIDTH = 150;
const MIN_COLUMN_WIDTH = 100;

type CampaignRequestsColumn = {
  name: string;
  title: string;
  getCellValue?: (row: Record<string, unknown>, columnName: string) => unknown;
};

type CampaignRequestsColumnExtension = {
  columnName: string;
  width: number | string;
  visible?: boolean;
  align?: string;
  minWidth?: number;
  maxWidth?: number;
};

type CampaignRequestsTableInfo = {
  columns: CampaignRequestsColumn[];
  columnExtensions: CampaignRequestsColumnExtension[];
  rows: Record<string, unknown>[];
  data: CampaignInfoOverview[];
  totalCount: number;
  pageSize?: number;
  currentPage: number;
  totalPages?: number;
  pageSizes?: number[];
};

const CampaignRequestsTable: React.FC<SearchTableRemoteProps> = (props) => {
  const { chipFilter, setSelectedOption, selectedOption = [], setCampaignRequests = () => null, setSubCampaignRequests = () => null, timeZone = UTC,
    setFilters = () => null, filterOverview, sortOverview, campaignStatuses = [], savedPageSize = 10,
    resetFilter, resetData, expandedRows = [], treeDataConfig, widthColumns, orderColumns,
    visibilityColumns, filterColumnExtensions, defaultMergeProperty, setOverviewFiltersFocus, tableTranslations,
  } = props;

  const { t, i18n } = useTranslation();
  const lang = useAppLanguage();
  const { goTo } = useGoTo();
  const { addToast } = useToastContext();

  const [tableInfo, setTableInfo] = useState<CampaignRequestsTableInfo>(defaultTableInfo(false, true, false, '', t, savedPageSize, { lang, options: tableTranslations }));
  const [loading, setLoading] = useState(true);
  const [isfirstLoad, setIsFirstLoad] = useState(true);

  const { columns, columnExtensions, rows, data, totalCount, pageSize = savedPageSize, currentPage = 0 } = tableInfo;

  const prevSelectionRef = useRef<(string | number)[]>([]);
  const loadingResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setTableInfo((prev) => ({ ...prev, pageSize: savedPageSize }));
  }, [savedPageSize]);

  useEffect(() => {
    setLoading(true);
    debouncedGetData(filters, 0, pageSize, sorting, chipFilter);
  }, [filterOverview, sortOverview, savedPageSize, chipFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (resetFilter && resetFilter > 0) {
      const resetFilters: GlobalFilters = {
        filterOverview: JSON.stringify(defaultFilterValues(false, '', true, false)),
        sortOverview: undefined,
      };
      setFilters(resetFilters);
    }
  }, [resetFilter, setFilters]);

  useEffect(() => {
    if (resetData) {
      setLoading(true);
      getTableData(filters, currentPage, pageSize, sorting, chipFilter);
    }
  }, [resetData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setTableInfo((prev) => ({
      ...prev,
      columns: defaultTableInfo(false, true, false, '', t, undefined, { lang, options: tableTranslations }).columns,
      rows: mapCampaingsRows(data, lang, timeZone, t, <i className="di icon-pfeil-chevron-rechts" />),
    }));
  }, [lang, timeZone, tableTranslations]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    prevSelectionRef.current = selectedOption.map((option) => option.id);
  }, [selectedOption]);

  useEffect(() => () => {
    if (loadingResetTimerRef.current) clearTimeout(loadingResetTimerRef.current);
  }, []);

  const defaultWidthColumns = useMemo<TableColumnWidthInfo[]>(
    () => columnExtensions.map((extension) => ({ columnName: extension.columnName, width: Number(extension.width) })),
    [columnExtensions],
  );
  const defaultVisibleColumns = useMemo<VisibilityColumnsOverview[]>(
    () => columnExtensions.map((extension) => ({ columnName: extension.columnName, visible: extension.visible !== false })),
    [columnExtensions],
  );

  const [localConfig, setLocalConfig] = useState<{ columnsWidth: TableColumnWidthInfo[]; columnsOrder: string[]; hiddenColumns: string[] }>(() => ({
    columnsWidth: widthColumns
      ? columns.map((column) => widthColumns.find((widthColumn) => widthColumn.columnName === column.name) ?? defaultWidthColumns.find((defaultColumn) => defaultColumn.columnName === column.name))
        .filter((item): item is TableColumnWidthInfo => !!item)
      : defaultWidthColumns,
    columnsOrder: orderColumns
      ? columnsOrderMerge(orderColumns, columns.map((orderColumn) => orderColumn.name), defaultMergeProperty)
      : columns.map((column) => column.name),
    hiddenColumns: (visibilityColumns
      ? mergeColumnExtensions(visibilityColumns, defaultVisibleColumns)
      : defaultVisibleColumns)
      .filter((column) => !column.visible)
      .map((column) => column.columnName),
  }));

  useEffect(() => {
    const columnsVisibility: VisibilityColumnsOverview[] = columns.map((column) => ({
      columnName: column.name,
      visible: !localConfig.hiddenColumns.includes(column.name),
    }));
    const configToSave: GlobalFilters = {
      orderColumnsOverview: JSON.stringify(localConfig.columnsOrder),
      widthColumnsOverview: JSON.stringify(localConfig.columnsWidth),
      visibilityColumnsOverview: JSON.stringify(columnsVisibility),
    };

    setFilters(configToSave);
  }, [localConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  const freeTextFilter = useMemo(
    () => filterOverview?.find((filter) => filter.columnName === filterField.FREE_TEXT && typeof filter.value === 'string'),
    [filterOverview],
  );

  const campaignTitleCellRenderer = useMemo(() => buildCampaignTitleCellRenderer(freeTextFilter, false), [freeTextFilter]);

  const enhancedTreeDataConfig = useMemo<TreeDataConfig<Record<string, unknown>> | undefined>(
    () => (treeDataConfig ? { ...treeDataConfig, innerCellRenderer: campaignTitleCellRenderer } : treeDataConfig),
    [treeDataConfig, campaignTitleCellRenderer],
  );

  const filters = useMemo(() => {
    const initial: Record<string, unknown> = defaultFilter(false, '', true, false) as unknown as Record<string, unknown>;

    return filterOverview?.reduce((acc, filter) => ({
      ...acc,
      [mapColumn(filter.columnName)]: Array.isArray(filter.value)
        ? filter.value
        : (typeof filter.value === 'string' ? filter.value : ''),
    }), initial) || initial;
  }, [filterOverview]);

  const filterModel = useMemo(() => buildFilterModel(filterOverview), [filterOverview]);

  const sorting = useMemo(() => buildColumnSorting(sortOverview), [sortOverview]);

  const fetchCampaigns = useApiRequest(campaignServices.getCampaignRequestsWithOpenSearch);

  const getCampaigns = useCallback((
    filter: Record<string, unknown>,
    page: number,
    size: number,
    sort: string,
    sortDir: string,
    childSort: string,
    childSortDir: string,
    filterChip: string,
  ) => {
    
    if (loadingResetTimerRef.current) {
      clearTimeout(loadingResetTimerRef.current);
      loadingResetTimerRef.current = null;
    }

    const filterKey = findFilterKey(sort) as string;
    const needTranslation = filterKey === 'campaigns.title.translations';

    fetchCampaigns(
      filterChip,
      buildOpensearchQueryData(filter, false),
      page,
      size,
      processSorting(childSort ? (opensearchFieldMapping.created as string) : filterKey),
      childSort ? CAPITAL_ASC : sortDir,
      processSorting(childSort),
      childSortDir,
      needTranslation ? lang : false,
    ).then((res) => {
      if (!res) return;

      const newData: CampaignInfoOverview[] = res.content;
      const newRows = mapCampaingsRows(newData, lang, timeZone, t, <i className="di icon-pfeil-chevron-rechts" />);

      setTableInfo((prev) => ({
        ...prev,
        data: newData,
        rows: newRows,
        totalPages: res.totalPages,
        currentPage: res.number,
        pageSize: res.pageSize || res.size || prev.pageSize || 50,
        totalCount: res.totalElements,
      }));
      setCampaignRequests(newData);
      setSubCampaignRequests(newData.flatMap((campaign) => campaign.subCampaigns) as SubCampaignInfoOverview[]);
      setIsFirstLoad(false);
    }).catch((error) => {
      if (error?.name !== 'AbortError' && error?.message) addToast(error.message, 'ERROR');
    }).finally(() => {
      loadingResetTimerRef.current = setTimeout(() => {
        loadingResetTimerRef.current = null;
        setLoading(false);
      }, 100);
    });
  }, [fetchCampaigns, lang, timeZone, t, setCampaignRequests, setSubCampaignRequests, addToast]);

  const getTableData = useCallback((
    filter: Record<string, unknown>,
    page: number,
    rowsPerPage: number,
    sort: ColumnSorting[],
    filterChip?: string,
  ) => {
    const mainSort = sort[0] ?? { columnName: CREATED, direction: DESC_ORDER };
    const isSubCR = mainSort.columnName === otherField[1].key;
    const sortColumnName = mainSort.columnName;
    const sortDirection = mainSort.direction?.toUpperCase() || CAPITAL_DESC;

    getCampaigns(
      filter,
      page,
      rowsPerPage,
      sortColumnName,
      sortDirection,
      isSubCR ? otherField[1].value : '',
      isSubCR ? sortDirection : '',
      filterChip || '',
    );
  }, [getCampaigns]);

  const getTableDataRef = useRef(getTableData);

  useEffect(() => {
    getTableDataRef.current = getTableData;
  }, [getTableData]);

  const debouncedGetData = useMemo(
    () => debounce(
      (filter: Record<string, unknown>, page: number, rowsPerPage: number, sort: ColumnSorting[], filterChip?: string) =>
        getTableDataRef.current(filter, page, rowsPerPage, sort, filterChip),
      getTimeToDebounce(true, isfirstLoad),
    ),
    [isfirstLoad],
  );

  const handleFilterModelChange = useCallback((newFilterModel: Record<string, unknown>) => {
    setLoading(true);
    setFilters({ filterOverview: JSON.stringify(buildFilterOverview(newFilterModel)) });
  }, [setFilters]);

  const handleSortModelChange = useCallback((newSorting: ColumnSorting[]) => {
    setLoading(true);
    setFilters({ sortOverview: JSON.stringify(buildSortOverview(newSorting)) });
  }, [setFilters]);

  const allRowsById = useMemo(() => {
    const map = new Map<string, Record<string, unknown>>();
    const walk = (row: Record<string, unknown>) => {
      const { id } = row;

      if (typeof id === 'string' || typeof id === 'number') {
        map.set(String(id), row);
      }
      const childRows = row.childRows as Record<string, unknown>[] | undefined;
      childRows?.forEach(walk);
    };
    rows.forEach(walk);

    return map;
  }, [rows]);

  const isParentRow = (row: Record<string, unknown> | undefined) =>
    !!row && !row.isSubCRChildren && !row.thirdLevelRow && !row.fourthLevelRow;

  const allParentIds = useMemo(
    () => [...allRowsById.values()].filter(isParentRow).map((row) => String(row.id)),
    [allRowsById],
  );

  const handleSelectionChange = useCallback((selectedValues: unknown[]) => {
    const newSelection = selectedValues.filter((value): value is string | number =>
      typeof value === 'string' || typeof value === 'number',
    );
    const rowClicked = newSelection.filter((id) => !prevSelectionRef.current.includes(id));
    const newSelectedParents = newSelection.filter((id) => isParentRow(allRowsById.get(String(id))));
    const prevSelectedParents = prevSelectionRef.current.filter((id) =>
      isParentRow(allRowsById.get(String(id))),
    );
    const allParentsSelected = allParentIds.length > 0 && newSelectedParents.length === allParentIds.length;
    const parentsAlreadySelected = allParentIds.length > 0 && prevSelectedParents.length === allParentIds.length;
    let finalSelection = newSelection;

    if (rowClicked.length > 1 || (rowClicked.length === 1 && allParentsSelected)) {
      if (parentsAlreadySelected) {
        finalSelection = [];
      } else if (allParentsSelected) {
        finalSelection = allParentIds;
      } else {
        finalSelection = newSelectedParents;
      }
    } else if (rowClicked.length === 1) {
      const lastClicked = rowClicked[0];
      const lastRow = allRowsById.get(String(lastClicked));
      const newHasSubCR = newSelection.some((id) => !!allRowsById.get(String(id))?.isSubCRChildren);
      const newHasParentCR = newSelection.some((id) => isParentRow(allRowsById.get(String(id))));

      if (lastRow?.isSubCRChildren && newHasParentCR) {
        finalSelection = [lastClicked];
      } else if (!lastRow?.isSubCRChildren && newHasSubCR) {
        finalSelection = [lastClicked];
      }
    }

    prevSelectionRef.current = finalSelection;
    setSelectedOption(finalSelection.map(String));
  }, [setSelectedOption, allRowsById, allParentIds]);

  const isRowSelectable = useCallback((node: IRowNode<Record<string, unknown> & { onClick?: () => void }>) => {
    const rowData = node.data;

    return !rowData?.thirdLevelRow && !rowData?.fourthLevelRow;
  }, []);

  const handlePageChange = useCallback((_: unknown, newPage: number) => {
    setLoading(true);
    getTableData(filters, newPage, pageSize, sorting, chipFilter);
  }, [getTableData, filters, pageSize, sorting, chipFilter]);

  const handleRowsPerPageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    const rowsPerPageFilters: GlobalFilters & { rowsPerPageOverview: number } = { rowsPerPageOverview: newPageSize };

    setFilters(rowsPerPageFilters);
    setTableInfo((prev) => ({ ...prev, pageSize: newPageSize }));
  }, [setFilters]);

  const handleColumnWidthsChange = useCallback((newWidths: Record<string, number>) => {
    const widthColumnsUpdate: TableColumnWidthInfo[] = Object.entries(newWidths).map(([columnName, width]) => ({ columnName, width }));
    setLocalConfig((prev) => ({ ...prev, columnsWidth: widthColumnsUpdate }));
  }, []);

  const handleColumnsOrderChanged = useCallback((newOrder: string[]) => {
    setLocalConfig((prev) => ({ ...prev, columnsOrder: newOrder }));
  }, []);

  const handleColumnVisibilityChanged = useCallback((newHidden: string[]) => {
    setLocalConfig((prev) => ({ ...prev, hiddenColumns: newHidden }));
  }, []);

  const handleResetColumnsSettings = useCallback(() => {
    setLocalConfig({
      columnsWidth: defaultWidthColumns,
      columnsOrder: columns.map((column) => column.name),
      hiddenColumns: defaultVisibleColumns.filter((column) => !column.visible).map((column) => column.columnName),
    });
  }, [defaultWidthColumns, defaultVisibleColumns, columns]);

  const columnsOrder = useMemo(() => localConfig.columnsOrder, [localConfig.columnsOrder]);
  const columnsWidth = useMemo(() => localConfig.columnsWidth, [localConfig.columnsWidth]);
  const hiddenColumns = useMemo(() => localConfig.hiddenColumns, [localConfig.hiddenColumns]);

  const sortedColumns = useMemo(() => {
    const orderMap = new Map(columnsOrder.map((name, index) => [name, index]));

    return [...columns].sort((a, b) => (orderMap.get(a.name) ?? 0) - (orderMap.get(b.name) ?? 0));
  }, [columns, columnsOrder]);

  const columnsConfig = useMemo<ColumnConfig[]>(() => sortedColumns.map((column) => {
    const extension = columnExtensions.find((ext) => ext.columnName === column.name);
    const widthInfo = columnsWidth.find((widthColumn) => widthColumn.columnName === column.name);
    const width = widthInfo?.width !== undefined
      ? Number(widthInfo.width)
      : (extension?.width !== undefined ? Number(extension.width) : undefined);
    const align = extension?.align as AlignValue | undefined;
    const { getCellValue } = column;

    let filterType: ColumnConfig['filterType'];
    let filterComponent: ColumnConfig['filterComponent'];
    let filterParams: ColumnConfig['filterParams'];
    let floatingFilterComponent: ColumnConfig['floatingFilterComponent'];
    let floatingFilterComponentParams: ColumnConfig['floatingFilterComponentParams'];
    let valueFormatter: ColumnConfig['valueFormatter'];
    let headerComponent: ColumnConfig['headerComponent'];
    let headerComponentParams: ColumnConfig['headerComponentParams'];

    if (column.name === OVERVIEW_COLUMNS.REWORK_NOT_STARTED) {
      headerComponent = HeaderWithTooltip;
      headerComponentParams = { tooltipText: t('plantFeedback.reworkNotStartedTooltip'), isWarningTooltip: false };
    }

    if (column.name === OVERVIEW_COLUMNS.ESTIMATED_CARS) {
      headerComponent = HeaderWithTooltip;
      headerComponentParams = { tooltipText: t('plantFeedback.estimatedCarsTooltip'), isWarningTooltip: false };
    }

    if (column.name === STATUS) {
      filterComponent = StatusFilterAgGrid;
      filterParams = { statuses: campaignStatuses };
      floatingFilterComponent = StatusFloatingFilter;
      floatingFilterComponentParams = { statuses: campaignStatuses };
      const vansStatuses = new Set<string>([
        statusCampaign.IN_VAN_PLANT_IMPLEMENTATION,
        statusCampaign.IN_VAN_CAUSER_APPROVAL,
        statusCampaign.IN_VAN_PLANT_VALIDATION,
        statusSubCampaign.IN_VAN_PLANT_COORDINATION,
      ]);

      valueFormatter = ({ value }: { value: string }) => {
        if (typeof value !== 'string') return value;
        const campaignKey = `campaign.${value}`;
        const subCampaignKey = `subCampaign.${value}`;
        const workflowKey = `workflow.${value}`;
        const label = i18n.exists(campaignKey)
          ? t(campaignKey)
          : (i18n.exists(subCampaignKey)
            ? t(subCampaignKey)
            : (i18n.exists(workflowKey) ? t(workflowKey) : value));

        return vansStatuses.has(value)
          ? `${label}${VANS_LABEL}`
          : label;
      };
    } else if ([VERSION_DATE, CREATED].includes(column.name)) {
      filterType = FILTER_TYPES.DATE;
      filterParams = DATE_FILTER_PARAMS;
    } else if (buttonTriggerLateralFilter.includes(column.name)) {
      filterType = FILTER_TYPES.TEXT;
      floatingFilterComponent = LateralFilterButton;
      floatingFilterComponentParams = {
        filterParams: {
          setOverviewFiltersFocus,
          field: HEADER_TO_FILTER[column.name],
        },
      };
    }

    return {
      name: column.name,
      title: column.title,
      flex: 1,
      width: width ?? DEFAULT_COLUMN_WIDTH,
      minWidth: extension?.minWidth ?? Math.max(width ?? DEFAULT_COLUMN_WIDTH, MIN_COLUMN_WIDTH),
      maxWidth: extension?.maxWidth,
      align,
      filterType,
      filterComponent,
      filterParams,
      ...([VERSION_DATE, CREATED].includes(column.name)
        ? {
          filterValueGetter: ({ data: row }: { data: Record<string, unknown> }) => (
            row[column.name === VERSION_DATE ? VERSION_DATE_RAW : CREATED_RAW] as string | undefined
          ) || (row[column.name] as string | undefined) || '',
        }
        : {}),
      floatingFilterComponent,
      floatingFilterComponentParams,
      ...(column.name === STATUS ? { suppressFloatingFilterButton: true, suppressHeaderFilterButton: true } : {}),
      valueFormatter,
      headerComponent,
      headerComponentParams,
      cellRenderer: column.name === OVERVIEW_COLUMNS.BLOCKING_LOCATIONS
        ? ({ value }: { value: unknown }) => (typeof value === 'string' ? value : null)
        : column.name === STATUS
          ? undefined
          : ({ value, data }: { value: unknown; data: Record<string, unknown> }) => (
            <Link to={createRowLink(data, false, freeTextFilter)}>
              {(getCellValue ? getCellValue(data, column.name) : value) as React.ReactNode}
            </Link>
          ),
    };
  }), [sortedColumns, columnExtensions, columnsWidth, freeTextFilter, setOverviewFiltersFocus, t, i18n, campaignStatuses]);

  const list = useMemo(
    () => rows.map((row) => ({ ...row, onClick: () => goTo(createRowLink(row, false, freeTextFilter)) })),
    [rows, freeTextFilter, goTo],
  );

  const selectedIds = useMemo(() => selectedOption.map((option) => option.id), [selectedOption]);

  return (
    <>
      <TableListEditableNew<Record<string, unknown> & { onClick?: () => void }>
        list={list}
        columns={columnsConfig}
        columnSorting={sorting}
        onSortModelChange={handleSortModelChange}
        filteringStateColumns={filterColumnExtensions}
        isFiltering
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        loading={loading}
        hiddenColumns={hiddenColumns}
        hasCheckbox
        columnId={COLUMN_ID}
        selection={selectedIds}
        rowSelectionMode={TABLE_ROW_SELECTION.MULTIPLE}
        onSelectionChange={handleSelectionChange}
        tableHeight="calc(100vh - 220px)"
        showColumnVisibilityButton
        treeDataConfig={enhancedTreeDataConfig}
        expandedRows={expandedRows}
        isRowSelectable={isRowSelectable}
        allowColumnMove
        animateRows={false}
        onColumnWidthsChange={handleColumnWidthsChange}
        onColumnsOrderChanged={handleColumnsOrderChanged}
        onColumnVisibilityChanged={handleColumnVisibilityChanged}
        onResetColumnsSettings={handleResetColumnsSettings}
        isPaginated={false}
      />
      <TablePagination
        component="div"
        count={totalCount}
        page={currentPage}
        onPageChange={handlePageChange}
        rowsPerPage={pageSize}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={PAGE_SIZES}
        labelRowsPerPage={t('dialog.rowsPerPage')}
      />
    </>
  );
};

export default CampaignRequestsTable;
